export { StoreProvider, createMfeStore } from './chunk-SEDIOAWQ.js';
import { useSelector, useDispatch } from 'react-redux';
import { createApi } from '@reduxjs/toolkit/query/react';
import { getContextClient } from '@quickbee/api-client';
import { platformEvents } from '@quickbee/events';
import { createSlice } from '@reduxjs/toolkit';

function createTypedHooks() {
  return {
    useAppDispatch: () => useDispatch(),
    useAppSelector: useSelector
  };
}

// src/api/types.ts
function isApiClientErrorLike(error) {
  return typeof error === "object" && error !== null && "message" in error && typeof error.message === "string";
}

// src/api/baseQuery.ts
function normalizeError(error) {
  if (isApiClientErrorLike(error)) {
    return {
      status: error.status,
      code: error.code,
      message: error.message,
      details: error.details
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: "Unknown error" };
}
function createDomainBaseQuery(client) {
  return async (args, api) => {
    const requestArgs = typeof args === "string" ? { url: args } : args;
    try {
      const data = await client.request({
        ...requestArgs,
        method: requestArgs.method ?? "GET",
        ...api?.signal ? { signal: api.signal } : {}
      });
      return { data: data ?? null };
    } catch (error) {
      return { error: normalizeError(error) };
    }
  };
}
function createDomainApi(config) {
  const { client, ...createApiOptions } = config;
  return createApi({
    ...createApiOptions,
    baseQuery: createDomainBaseQuery(client)
  });
}
function createContextApi(config) {
  const { context, mocks, publicClient, ...domainApiConfig } = config;
  return createDomainApi({
    ...domainApiConfig,
    client: getContextClient({
      context,
      ...mocks ? { mocks } : {},
      ...publicClient ? { publicClient: true } : {}
    })
  });
}

// src/api/cursorPagination.ts
function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const entries = Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a.localeCompare(b));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`;
}
function createCursorPagedEndpoint() {
  return {
    serializeQueryArgs: ({ queryArgs, endpointName }) => {
      const { cursor: _cursor, ...rest } = queryArgs ?? {};
      return `${endpointName}(${stableStringify(rest)})`;
    },
    merge: (currentCache, incoming, { arg }) => {
      if (!arg?.cursor) {
        currentCache.items = incoming.items;
        currentCache.pagination = incoming.pagination;
        return;
      }
      currentCache.items.push(...incoming.items);
      currentCache.pagination = incoming.pagination;
    },
    forceRefetch: ({
      currentArg,
      previousArg
    }) => currentArg?.cursor !== previousArg?.cursor
  };
}
function emptyPage() {
  return { items: [], pagination: { cursor: null, hasMore: false, total: 0 } };
}
function nextPageArgs(args, result) {
  if (!result?.pagination.hasMore || !result.pagination.cursor) return null;
  return { ...args, cursor: result.pagination.cursor };
}

// src/api/tags.ts
var LIST_ID = "LIST";
function providesList(type, result, idKey = "id") {
  const items = Array.isArray(result) ? result : result?.items ?? [];
  const tags = [{ type, id: LIST_ID }];
  for (const item of items) {
    const id = item?.[idKey];
    if (typeof id === "string" || typeof id === "number") {
      tags.push({ type, id });
    }
  }
  return tags;
}
function providesOne(type, id) {
  return id === void 0 ? [{ type, id: LIST_ID }] : [{ type, id }];
}
function invalidatesOne(type, id) {
  return id === void 0 ? [{ type, id: LIST_ID }] : [{ type, id }, { type, id: LIST_ID }];
}

// src/api/cachePolicy.ts
var CACHE_POLICY = {
  /** Live gold rate. Stale prices must never be transacted on (`PRICE_STALE`, §8.3). */
  livePricing: {
    keepUnusedDataFor: 15,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    pollingIntervalMs: 3e4
  },
  /** Ledger accounts, redeemable balances — money the user is looking at. */
  financialBalance: {
    keepUnusedDataFor: 60,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  },
  /** Tickets, disputes, loan leads, orders. */
  transactionalList: {
    keepUnusedDataFor: 120,
    refetchOnMountOrArgChange: true
  },
  /** Scheme catalogue, templates, feature flags — changes rarely, read constantly. */
  reference: {
    keepUnusedDataFor: 900,
    refetchOnMountOrArgChange: false
  },
  /** Generated reports and dashboards — expensive, explicitly refreshed. */
  report: {
    keepUnusedDataFor: 300,
    refetchOnMountOrArgChange: false
  }
};
var DEFAULT_SETTLE_DELAY_MS = 800;
function registerCrossContextInvalidation(options) {
  const { store, api, rules } = options;
  const timers = /* @__PURE__ */ new Set();
  const unsubscribes = [];
  for (const rule of rules) {
    const unsubscribe = platformEvents.on(rule.event, (payload) => {
      const tags = rule.invalidate(payload);
      if (tags.length === 0) return;
      const delay = rule.settleDelayMs ?? DEFAULT_SETTLE_DELAY_MS;
      const timer = setTimeout(() => {
        timers.delete(timer);
        store.dispatch(api.util.invalidateTags(tags));
      }, delay);
      timers.add(timer);
    });
    unsubscribes.push(unsubscribe);
  }
  return () => {
    unsubscribes.forEach((dispose) => dispose());
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
  };
}
function emitPlatformEvent(event, payload) {
  platformEvents.emit(event, payload);
}
function resetApiState(store, apis) {
  for (const api of apis) {
    store.dispatch(api.util.resetApiState());
  }
}
function bindAuthStateReset(store, apis) {
  const reset = () => resetApiState(store, apis);
  const disposers = [
    platformEvents.on("authStateChanged", reset),
    platformEvents.on("sessionExpired", reset)
  ];
  return () => disposers.forEach((dispose) => dispose());
}
function createDrawerSlice(name, options = {}) {
  const initialState = {
    isOpen: false,
    selectedId: options.initialSelectedId ?? null
  };
  return createSlice({
    name,
    initialState,
    reducers: {
      open: (state, action) => {
        state.isOpen = true;
        if (action.payload !== void 0) {
          state.selectedId = action.payload;
        }
      },
      close: (state) => {
        state.isOpen = false;
        state.selectedId = null;
      }
    }
  });
}
function createDraftSlice(name, initialDraft) {
  const initialState = {
    draft: initialDraft,
    loading: false
  };
  return createSlice({
    name,
    initialState,
    reducers: {
      setDraft: (state, action) => {
        state.draft = { ...state.draft, ...action.payload };
      },
      resetDraft: (state) => {
        state.draft = initialDraft;
      },
      setLoading: (state, action) => {
        state.loading = action.payload;
      }
    }
  });
}

export { CACHE_POLICY, DEFAULT_SETTLE_DELAY_MS, LIST_ID, bindAuthStateReset, createContextApi, createCursorPagedEndpoint, createDomainApi, createDomainBaseQuery, createDraftSlice, createDrawerSlice, createTypedHooks, emitPlatformEvent, emptyPage, invalidatesOne, isApiClientErrorLike, nextPageArgs, providesList, providesOne, registerCrossContextInvalidation, resetApiState };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map